import { useEffect, useMemo, useState } from 'react';
import { badgesApi, rewardsApi, ApiRequestError, type TierDefinition, type TierDefinitionUpdate } from '@/lib/api';

interface RewardTiersModalProps {
  isOpen: boolean;
  onClose: () => void;
}

function validateTiers(tiers: TierDefinition[]): string | null {
  if (tiers.length === 0) return 'No tiers found to save';

  const sorted = [...tiers].sort((a, b) => a.level - b.level);

  const tier0 = sorted.find((t) => t.level === 0);
  if (!tier0) return 'Tier 0 is required';
  if (tier0.nightsRequired !== 0) return 'Tier 0 nights required must be 0';

  for (const tier of sorted) {
    if (!tier.name || tier.name.trim().length < 1) return `Tier ${tier.level}: name is required`;
    if (!Number.isInteger(tier.nightsRequired) || tier.nightsRequired < 0) return `Tier ${tier.level}: nights must be a non-negative integer`;
    if (!tier.badgeKey || tier.badgeKey.trim().length < 1) return `Tier ${tier.level}: badge key is required`;
  }

  for (let i = 1; i < sorted.length; i++) {
    const prev = sorted[i - 1];
    const cur = sorted[i];
    if (cur.nightsRequired <= prev.nightsRequired) {
      return `Tier ${cur.level}: nights required must be greater than tier ${prev.level}`;
    }
  }

  return null;
}

export default function RewardTiersModal({ isOpen, onClose }: RewardTiersModalProps) {
  const nowYear = new Date().getFullYear();

  // Default to current year annual tiers.
  const [tierScope, setTierScope] = useState<string>(() => String(nowYear));

  const [availableYears, setAvailableYears] = useState<number[]>([]);
  const [tiers, setTiers] = useState<TierDefinition[]>([]);
  const [tiersSource, setTiersSource] = useState<'lifetime' | 'annual' | null>(null);

  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  // Used to force badge previews to refresh after uploads.
  const [previewNonce, setPreviewNonce] = useState(0);
  const [uploadFiles, setUploadFiles] = useState<Record<string, File | null>>({});
  const [uploading, setUploading] = useState<Record<string, boolean>>({});

  const yearOptions = useMemo(() => {
    const years = new Set<number>();
    availableYears.forEach((y) => years.add(y));
    years.add(nowYear);
    years.add(nowYear + 1);
    years.add(nowYear - 1);
    return [...years].sort((a, b) => b - a);
  }, [availableYears, nowYear]);

  const selectedYear = useMemo(() => {
    if (tierScope === 'lifetime') return null;
    const parsed = parseInt(tierScope, 10);
    return Number.isFinite(parsed) ? parsed : null;
  }, [tierScope]);

  useEffect(() => {
    if (!isOpen) return;

    setError(null);
    setSuccess(false);
    setPreviewNonce(0);

    const loadYears = async () => {
      try {
        const { years } = await rewardsApi.getYears();
        setAvailableYears(years || []);
      } catch {
        // Non-fatal: the dropdown will still include current/next year.
        setAvailableYears([]);
      }
    };

    loadYears();
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;

    const loadTiers = async () => {
      setIsLoading(true);
      setError(null);
      setSuccess(false);
      try {
        const result = tierScope === 'lifetime'
          ? await rewardsApi.getTiers(null)
          : await rewardsApi.getTiers(selectedYear);

        setTiersSource(result.source || null);
        setTiers([...result.tiers].sort((a, b) => a.level - b.level));
      } catch (err) {
        if (err instanceof ApiRequestError) {
          setError(err.message);
        } else {
          setError('Failed to load reward tiers');
        }
      } finally {
        setIsLoading(false);
      }
    };

    loadTiers();
  }, [isOpen, tierScope, selectedYear]);

  useEffect(() => {
    if (!isOpen) return;

    // When opening, default to current year annual tiers (unless user already chose).
    setTierScope((prev) => prev || String(nowYear));
  }, [isOpen, nowYear]);

  if (!isOpen) return null;

  const updateTier = (level: number, patch: Partial<TierDefinition>) => {
    setTiers((prev) => prev.map((t) => (t.level === level ? { ...t, ...patch } : t)));
  };

  const setUploadFile = (tierLevel: number, gender: 'male' | 'female', file: File | null) => {
    const key = `${tierLevel}-${gender}`;
    setUploadFiles((prev) => ({ ...prev, [key]: file }));
  };

  const handleUpload = async (tierLevel: number, gender: 'male' | 'female') => {
    const key = `${tierLevel}-${gender}`;
    const file = uploadFiles[key];
    if (!file) {
      setError('Select a PNG file to upload first.');
      return;
    }

    const tier = tiers.find((t) => t.level === tierLevel);
    if (!tier) {
      setError('Tier not found.');
      return;
    }

    setError(null);
    setUploading((prev) => ({ ...prev, [key]: true }));
    try {
      await badgesApi.upload({ badgeKey: tier.badgeKey.trim(), gender, file });
      setUploadFile(tierLevel, gender, null);
      setPreviewNonce(Date.now());
    } catch (err) {
      if (err instanceof ApiRequestError) {
        setError(err.message);
      } else {
        setError('Failed to upload badge image');
      }
    } finally {
      setUploading((prev) => ({ ...prev, [key]: false }));
    }
  };

  const handleSave = async () => {
    setError(null);
    setSuccess(false);

    const validationMessage = validateTiers(tiers);
    if (validationMessage) {
      setError(validationMessage);
      return;
    }

    const payload: TierDefinitionUpdate[] = tiers.map((t) => ({
      level: t.level,
      name: t.name,
      description: t.description,
      nightsRequired: t.nightsRequired,
      badgeKey: t.badgeKey,
    }));

    setIsSaving(true);
    try {
      if (tierScope === 'lifetime') {
        await rewardsApi.updateTiers(payload);
      } else if (selectedYear) {
        await rewardsApi.updateTiersForYear(selectedYear, payload);
      } else {
        throw new Error('Invalid year');
      }

      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);

      // Re-fetch to pick up any server-side normalization / source flag.
      const refreshed = tierScope === 'lifetime'
        ? await rewardsApi.getTiers(null)
        : await rewardsApi.getTiers(selectedYear);
      setTiersSource(refreshed.source || null);
      setTiers([...refreshed.tiers].sort((a, b) => a.level - b.level));
    } catch (err) {
      if (err instanceof ApiRequestError) {
        setError(err.message);
      } else {
        setError('Failed to save reward tiers');
      }
    } finally {
      setIsSaving(false);
    }
  };

  const isAnnual = tierScope !== 'lifetime' && !!selectedYear;
  const showFallbackNote = isAnnual && tiersSource === 'lifetime';

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-full items-center justify-center p-4">
        {/* Backdrop */}
        <div
          className="fixed inset-0 bg-black/50 transition-opacity"
          onClick={onClose}
        />

        {/* Modal */}
        <div className="relative bg-white rounded-lg shadow-xl max-w-5xl w-full max-h-[90vh] flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Reward Tiers</h2>
              <p className="text-sm text-gray-600 mt-1">
                Edit tier names, thresholds, and badge keys. Upload new badge images here as well.
              </p>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-500"
              aria-label="Close"
            >
              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-6 space-y-6">
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md text-sm">
                {error}
              </div>
            )}

            {success && (
              <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-md text-sm">
                Reward tiers saved successfully!
              </div>
            )}

            <div className="flex flex-col md:flex-row md:items-center gap-4">
              <div className="flex-1">
                <label className="label">Tier Set</label>
                <select
                  value={tierScope}
                  onChange={(e) => setTierScope(e.target.value)}
                  className="input"
                >
                  <option value="lifetime">Lifetime (all-time status)</option>
                  {yearOptions.map((y) => (
                    <option key={y} value={String(y)}>
                      {y} (annual badges)
                    </option>
                  ))}
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  Annual tiers affect badges and progress for that year. Lifetime tiers affect the overall status level.
                </p>
                {showFallbackNote && (
                  <p className="text-xs text-amber-700 mt-2">
                    This year has no custom tiers yet. You are viewing the lifetime defaults; saving will create annual tiers for {selectedYear}.
                  </p>
                )}
              </div>
            </div>

            {isLoading ? (
              <div className="flex justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
              </div>
            ) : (
              <div className="space-y-4">
                {tiers.map((tier) => (
                  <div
                    key={tier.level}
                    className="border border-gray-200 rounded-lg p-4 space-y-4"
                  >
                    <div className="flex items-center justify-between">
                      <div className="font-semibold text-gray-900">
                        Tier {tier.level}
                      </div>
                      <div className="text-xs text-gray-500">
                        Nights: {tier.nightsRequired}
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className="label">Name</label>
                        <input
                          type="text"
                          value={tier.name}
                          onChange={(e) => updateTier(tier.level, { name: e.target.value })}
                          className="input"
                        />
                      </div>

                      <div>
                        <label className="label">Nights Required</label>
                        <input
                          type="number"
                          min={0}
                          step={1}
                          value={tier.nightsRequired}
                          disabled={tier.level === 0}
                          onChange={(e) => {
                            const raw = e.target.value;
                            const parsed = raw === '' ? 0 : parseInt(raw, 10);
                            updateTier(tier.level, { nightsRequired: Number.isFinite(parsed) ? parsed : 0 });
                          }}
                          className="input"
                        />
                        {tier.level === 0 && (
                          <p className="text-xs text-gray-500 mt-1">
                            Tier 0 is always 0 nights.
                          </p>
                        )}
                      </div>

                      <div className="md:col-span-2">
                        <label className="label">Description</label>
                        <textarea
                          value={tier.description}
                          onChange={(e) => updateTier(tier.level, { description: e.target.value })}
                          className="input"
                          rows={2}
                        />
                      </div>

                      <div className="md:col-span-2">
                        <label className="label">Badge Key</label>
                        <input
                          type="text"
                          value={tier.badgeKey}
                          onChange={(e) => updateTier(tier.level, { badgeKey: e.target.value })}
                          className="input"
                          placeholder="tourist"
                        />
                        <p className="text-xs text-gray-500 mt-1">
                          Badge images resolve to <span className="font-mono">&lt;key&gt;-male.png</span> and <span className="font-mono">&lt;key&gt;-female.png</span>.
                        </p>
                      </div>

                      <div className="md:col-span-2">
                        <label className="label">Badge Images</label>
                        <div className="flex flex-col lg:flex-row gap-6 lg:items-start">
                          <div className="flex items-center gap-4">
                            <div className="space-y-2">
                              <div className="text-xs font-medium text-gray-500">Male</div>
                              <img
                                key={`${tier.level}-${tier.badgeKey}-male-${previewNonce}`}
                                src={`${badgesApi.getUrl(tier.badgeKey, 'male')}${previewNonce ? `?v=${previewNonce}` : ''}`}
                                alt={`${tier.badgeKey} male`}
                                className="w-28 h-20 object-cover rounded border border-gray-200 bg-gray-50"
                                onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                              />
                              <input
                                type="file"
                                accept="image/png"
                                onChange={(e) => setUploadFile(tier.level, 'male', e.target.files?.[0] || null)}
                                className="block text-sm"
                              />
                              <button
                                onClick={() => handleUpload(tier.level, 'male')}
                                disabled={!!uploading[`${tier.level}-male`]}
                                className="btn-secondary"
                                type="button"
                              >
                                {uploading[`${tier.level}-male`] ? 'Uploading...' : 'Upload Male PNG'}
                              </button>
                            </div>

                            <div className="space-y-2">
                              <div className="text-xs font-medium text-gray-500">Female</div>
                              <img
                                key={`${tier.level}-${tier.badgeKey}-female-${previewNonce}`}
                                src={`${badgesApi.getUrl(tier.badgeKey, 'female')}${previewNonce ? `?v=${previewNonce}` : ''}`}
                                alt={`${tier.badgeKey} female`}
                                className="w-28 h-20 object-cover rounded border border-gray-200 bg-gray-50"
                                onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                              />
                              <input
                                type="file"
                                accept="image/png"
                                onChange={(e) => setUploadFile(tier.level, 'female', e.target.files?.[0] || null)}
                                className="block text-sm"
                              />
                              <button
                                onClick={() => handleUpload(tier.level, 'female')}
                                disabled={!!uploading[`${tier.level}-female`]}
                                className="btn-secondary"
                                type="button"
                              >
                                {uploading[`${tier.level}-female`] ? 'Uploading...' : 'Upload Female PNG'}
                              </button>
                            </div>
                          </div>

                          <div className="text-xs text-gray-500">
                            <div className="font-medium text-gray-700">Notes</div>
                            <div className="mt-1">
                              Uploaded images are stored in R2 and served from the API. If you don’t upload anything, the system falls back to the built-in badges.
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="flex justify-end gap-3 p-6 border-t bg-gray-50">
            <button onClick={onClose} className="btn-secondary" type="button">
              Close
            </button>
            <button onClick={handleSave} disabled={isSaving} className="btn-primary" type="button">
              {isSaving ? 'Saving...' : 'Save Tiers'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

